import React, { useState } from "react";
import {
  Box,
  Button,
  Modal,
  ModalDialog,
  Stack,
  Textarea,
  Typography,
} from "@mui/joy";
import { theme } from "styles/theme";
import type { TaskEditorState } from "./taskEditorUtils";

export interface AIGenerateRequest {
  rawText: string;
  additionalInstructions: string;
  previousResult?: TaskEditorState;
}

interface TaskEditorAIGenerateModalProps {
  open: boolean;
  onClose: () => void;
  onSubmit: (request: AIGenerateRequest) => void;
}

export default function TaskEditorAIGenerateModal({
  open,
  onClose,
  onSubmit,
}: TaskEditorAIGenerateModalProps) {
  const [rawText, setRawText] = useState("");
  const [systemPrompt, setSystemPrompt] = useState("");

  const canSubmit = rawText.trim().length > 0;

  const handleSubmit = () => {
    if (!canSubmit) {
      return;
    }

    onSubmit({
      rawText: rawText.trim(),
      additionalInstructions: systemPrompt.trim(),
    });
    setRawText("");
    setSystemPrompt("");
    onClose();
  };

  const handleClose = () => {
    onClose();
  };

  return (
    <Modal open={open} onClose={handleClose}>
      <ModalDialog
        sx={{
          width: "860px",
          maxWidth: "94vw",
          height: "80vh",
          maxHeight: "80vh",
          px: "36px",
          pt: "28px",
          pb: "24px",
          borderRadius: "24px",
          border: 0,
          boxShadow: theme.shadow.lg,
          overflow: "hidden",
          display: "flex",
          flexDirection: "column",
        }}
      >
        <Typography
          variant="Body"
          sx={{
            fontWeight: 700,
            color: theme.vars.palette.Content.Strong,
            fontSize: "18px",
            mb: "4px",
            flexShrink: 0,
          }}
        >
          AI로 활동지 초안 생성
        </Typography>

        <Typography
          variant="Caption"
          sx={{
            color: theme.vars.palette.Content.Weak,
            mb: "20px",
            flexShrink: 0,
          }}
        >
          활동지 내용을 줄글로 입력하면 자동으로 블록과 컴포넌트를 구성합니다.
          생성 중에도 다른 작업을 계속할 수 있습니다.
        </Typography>

        <Stack spacing={2.5} sx={{ flex: 1, minHeight: 0, overflow: "auto" }}>
          <Stack spacing={0.75} sx={{ flex: 1, minHeight: 0 }}>
            <Typography
              variant="Caption"
              sx={{
                fontWeight: 700,
                color: theme.vars.palette.Content.Strong,
                flexShrink: 0,
              }}
            >
              활동지 내용 (Raw Text)
              <Box
                component="span"
                sx={{ color: "danger.500", ml: "2px" }}
              >
                *
              </Box>
            </Typography>
            <Textarea
              placeholder={`활동지 내용을 자유롭게 작성해주세요.\n\n예시:\n# 자동적 사고 알아보기\n\n## 1단계: 개념 이해\n자동적 사고란, 어떤 상황에서 자동으로 떠오르는 생각을 말합니다.\n이 생각은 사실이 아닐 수 있지만, 감정과 행동에 큰 영향을 미칩니다.\n\n다음은 자동적 사고의 예시입니다.\n[자동적 사고 mock]\n\n## 2단계: 나의 경험\n최근 부정적인 감정이 들었던 상황을 떠올려보세요.\n\n그때 어떤 상황이었나요?\n[입력: 상황을 구체적으로 적어주세요]\n\n어떤 감정이 느껴졌나요?\n[선택: 불안 / 슬픔 / 분노 / 두려움]`}
              value={rawText}
              onChange={(e) => setRawText(e.target.value)}
              sx={{
                flex: 1,
                width: "100%",
                fontSize: "14px",
                lineHeight: 1.7,
                "--Textarea-focusedThickness": "1px",
              }}
            />
          </Stack>

          <Stack spacing={0.75} sx={{ flexShrink: 0 }}>
            <Typography
              variant="Caption"
              sx={{
                fontWeight: 700,
                color: theme.vars.palette.Content.Strong,
              }}
            >
              추가 지시사항 (선택)
            </Typography>
            <Textarea
              placeholder={`컴포넌트 구성에 대한 추가 요청사항을 적어주세요.\n예: Quiz를 1개 이상 포함해주세요 / 이미지 컴포넌트를 2개 넣어주세요 / 분기(네/아니요)를 활용해주세요`}
              value={systemPrompt}
              onChange={(e) => setSystemPrompt(e.target.value)}
              minRows={2}
              maxRows={4}
              sx={{
                width: "100%",
                fontSize: "14px",
                lineHeight: 1.6,
                "--Textarea-focusedThickness": "1px",
              }}
            />
          </Stack>
        </Stack>

        <Stack
          direction="row"
          spacing={1}
          justifyContent="flex-end"
          sx={{ mt: "20px", flexShrink: 0 }}
        >
          <Button
            variant="Outlined"
            color="neutral"
            onClick={handleClose}
            sx={{ height: "42px", px: "20px" }}
          >
            취소
          </Button>
          <Button
            onClick={handleSubmit}
            disabled={!canSubmit}
            sx={{ height: "42px", px: "24px" }}
          >
            생성하기
          </Button>
        </Stack>
      </ModalDialog>
    </Modal>
  );
}
